import React from 'react';
import { Tag, Tooltip, Space } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  PauseCircleOutlined,
  QuestionCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';

interface HealthStatusTagProps {
  status: 'healthy' | 'degraded' | 'unhealthy' | 'stopped' | 'unknown' | 'error';
  successRate?: number;
  showTooltip?: boolean;
  showDetails?: boolean;
}

/**
 * Component to display health status with appropriate color and icon
 */
const HealthStatusTag: React.FC<HealthStatusTagProps> = ({
  status,
  successRate,
  showTooltip = true,
  showDetails = false
}) => {
  const { t: indexerT } = useTranslationWithNamespace('indexer');

  // Define status configurations
  const statusConfig: Record<string, {
    color: string;
    icon: React.ReactNode;
    text: string;
    tooltip: string
  }> = {
    'healthy': {
      color: 'green',
      icon: <CheckCircleOutlined />,
      text: indexerT('health.healthy', 'Healthy'),
      tooltip: indexerT('health.healthyTooltip', 'Indexer is functioning normally')
    },
    'degraded': {
      color: 'orange',
      icon: <WarningOutlined />,
      text: indexerT('health.degraded', 'Degraded'),
      tooltip: indexerT('health.degradedTooltip', 'Indexer is experiencing some issues but still functioning')
    },
    'unhealthy': {
      color: 'red',
      icon: <CloseCircleOutlined />,
      text: indexerT('health.unhealthy', 'Unhealthy'),
      tooltip: indexerT('health.unhealthyTooltip', 'Indexer is experiencing critical issues')
    },
    'stopped': {
      color: 'blue',
      icon: <PauseCircleOutlined />,
      text: indexerT('health.stopped', 'Stopped'),
      tooltip: indexerT('health.stoppedTooltip', 'Indexer is intentionally stopped')
    },
    'error': {
      color: 'red',
      icon: <CloseCircleOutlined />,
      text: indexerT('health.error', 'Error'),
      tooltip: indexerT('health.errorTooltip', 'Indexer encountered an error')
    },
    'unknown': {
      color: 'default',
      icon: <QuestionCircleOutlined />,
      text: indexerT('health.unknown', 'Unknown'),
      tooltip: indexerT('health.unknownTooltip', 'Health status is unknown')
    }
  };

  // Get config for current status or use default
  const config = statusConfig[status] || statusConfig.unknown;

  // Create the tag
  const tag = (
    <Tag color={config.color} icon={config.icon}>
      {config.text}
    </Tag>
  );

  // Create details tag if needed
  const detailsTag = showDetails && successRate !== undefined ? (
    <Tag color="default" icon={<InfoCircleOutlined />}>
      {`${Math.round(successRate)}% ${indexerT('health.successRate', 'Success')}`}
    </Tag>
  ) : null;

  // Wrap in tooltip if needed
  if (showTooltip) {
    const tooltipContent = (
      <div>
        <div>{config.tooltip}</div>
        {successRate !== undefined && (
          <div>{indexerT('health.successRateDetail', 'API Success Rate: {{rate}}%', { rate: Math.round(successRate) })}</div>
        )}
      </div>
    );

    return (
      <Tooltip title={tooltipContent}>
        <Space>
          {tag}
          {detailsTag}
        </Space>
      </Tooltip>
    );
  }

  return (
    <Space>
      {tag}
      {detailsTag}
    </Space>
  );
};

export default HealthStatusTag;
